import qs from 'query-string';
import HTTPMethods from './http';
import { FetchConfig } from 'types';
import Auth from '../auth/index';
import postmanMockFilter, { ModifiedFetchConfig } from '../auth/postman';
import { setCookie, getCookie } from '../auth/cookies';
import cookieNames from '../auth/stashDefaults';

export function buildURL(
	{ root, path = '', params }: Partial<FetchConfig> = {},
	{ uuid, accessToken }: ReturnType<typeof Auth.get>
) {
	const hasParams = params && Object.keys(params).length;
	const encodedParams = hasParams
		? `?${qs.stringify(params as Record<string, any>, {
				skipNull: true,
				skipEmptyString: true,
		  })}`
		: '';
	const fallbackUuid = uuid ? uuid : getUuidFromToken(accessToken);
	// log out if uuid is missing
	if (!fallbackUuid) {
		Auth.logOut('missing user id');
	}
	if (!getCookie(cookieNames.uuid)) {
		setCookie(cookieNames.uuid, fallbackUuid);
	}

	return `${root}${path}${encodedParams}`
		.replace(':user_id', fallbackUuid || '')
		.replace(':uuid', fallbackUuid || '');
}

export function buildFetchConfig({
	method = HTTPMethods.GET,
	headers = {},
	body,
	accessToken,
	mode = 'cors',
	...rest
}: FetchConfig | Partial<FetchConfig>) {
	const authToken = accessToken || Auth.get().accessToken;
	const authHeader: { [key: string]: string } =
		!!authToken && !checkExpired(authToken) ? { Authorization: authToken } : {};

	return {
		headers: {
			'Content-Type': 'application/json',
			...authHeader,
			...headers,
		},
		mode: mode || 'cors',
		body: body && JSON.stringify(body),
		method,
		...rest,
	} as const;
}

export async function sendRequest(
	url: string,
	fetchArgs: FetchConfig | Partial<FetchConfig>
) {
	const fetchConfig = buildFetchConfig(fetchArgs);

	if (process.env.NODE_ENV !== 'production') {
		if (fetchArgs.postman)
			return fetch(...postmanMockFilter(url, fetchConfig as ModifiedFetchConfig));
	}

	return fetch(url, fetchConfig);
}

export async function getJSON(res: Response) {
	if (res.status === 204) return '';
	const json = await res.json();
	return res.ok ? Promise.resolve(json) : Promise.reject(json);
}

export async function handleSessionChange(json: { [key: string]: any }) {
	if (json.api_key)
		Auth.set({
			accessToken: json.api_key.access_token,
			uuid: json.api_key.uuid,
			userId: json.api_key.user_id,
		});
}

function parseJwt(token: string) {
	try {
		return JSON.parse(atob(token.split('.')[1]));
	} catch (e) {
		return null;
	}
}

function getExpiry(accessToken: string) {
	const parsed = parseJwt(accessToken.replace('Bearer ', ''));
	const { exp } = parsed;
	// multiply by 1000 to account for difference between Rails and JS dates
	return exp * 1000;
}

export function checkExpired(accessToken: string | undefined) {
	if (!accessToken) {
		return false;
	}
	const expiry = getExpiry(accessToken);
	return new Date().getTime() >= expiry - 2000;
}

export function getUuidFromToken(accessToken: string | undefined) {
	if (!accessToken) {
		return null;
	}
	const parsed = parseJwt(accessToken.replace('Bearer ', ''));
	if (parsed) {
		return parsed.sub;
	} else {
		return null;
	}
}

export const generateRandomString = (complexity: number | undefined = 3) => {
	return Array.apply(null, Array(complexity))
		.map(() => {
			return Math.random().toString(36).slice(2);
		})
		.join('');
};
